<template lang="pug">
v-navigation-drawer.z-50.activity-log-drawer(v-model="showDrawer" mobile-breakpoint="xs" width="474" temporary right app touchless :height="drawerHeight")
  .d-flex.flex-column.fill-height
    //- Header
    .d-flex.align-center.justify-space-between.px-6.py-5.sticky.top-0.white.z-1
      .text-lg.font-semibold.neutral700--text Logs
      v-card.round-lg(outlined link width="28" height="28" @click="closeDrawer")
        .d-flex.align-center.justify-center.fill-height
          v-icon(color="neutral700" size="16") mdi-close

    //- Content
    .flex-grow-1.overflow-y-auto.px-6.pb-6
      //- Loading state
      template(v-if="isLoading")
        .d-flex.justify-center.py-8
          v-progress-circular(:width="2" :size="30" color="primary" indeterminate)

      //- Empty state
      template(v-else-if="!logs.length")
        .d-flex.flex-column.align-center.justify-center.py-16
          v-icon(size="48" color="neutral400") mdi-clipboard-text-outline
          .text-sm.neutral500--text.mt-4 No activity logs found

      //- Log entries
      template(v-else)
        .log-entries
          template(v-for="(log, idx) in logs")
            .log-entry-wrapper(:key="log.id")
              //- Log entry card
              v-card.log-entry-card.round-lg(outlined)
                .d-flex.align-start.pa-3.gap-3
                  //- Icon
                  .log-icon.d-flex.align-center.justify-center.rounded-circle.neutral300
                    v-icon(size="16" color="neutral500") {{ getIcon(log.tag) }}

                  //- Content
                  .flex-grow-1.min-width-0
                    .d-flex.align-start.justify-space-between
                      .flex-grow-1.min-width-0.pr-2
                        .text-sm.neutral700--text.log-activity {{ log.activity }}
                        .d-flex.align-center.mt-1
                          v-icon(size="12" color="neutral500") $yzi-user-edit
                          .text-xs.neutral500--text.ml-1 {{ getUserName(log) }},
                          .text-xs.neutral500--text.ml-1 {{ log.created | humanizeDate }}, {{ log.created | displayTime2 }}
                      //- Add/Edit note button
                      yz-btn(:label="log.note ? 'Edit note' : 'Add note'" size="sm" outlined @click="onAddNote(log)")

                    //- Note section (if exists)
                    template(v-if="log.note")
                      .mt-3.pa-3.rounded-lg.neutral200
                        .d-flex.align-center.justify-space-between
                          .text-xs.font-semibold.neutral700--text Note
                          v-menu(offset-y left min-width="120")
                            template(v-slot:activator="{ on }")
                              yz-btn(v-on="on" outlined size="sm" icon="mdi-dots-horizontal")
                            v-list.py-0
                              v-list-item(@click="onEditNote(log)")
                                v-list-item-title.text-sm Edit
                              v-divider
                              v-list-item(@click="onDeleteNote(log)")
                                v-list-item-title.text-sm.danger500--text Delete
                        .text-xs.neutral700--text.mt-1 {{ log.note }}
                        .d-flex.align-center.mt-1(v-if="log.note_by")
                          v-icon(size="12" color="neutral500") $yzi-user-edit
                          .text-xxs.font-medium.neutral500--text.ml-1 {{ getNoteUserName(log.note_by) }},
                          .text-xxs.font-medium.neutral500--text.ml-1 {{ log.note_at | humanizeDate }}, {{ log.note_at | displayTime2 }}

              //- Connector line (except for last item)
              .connector-line(v-if="idx < logs.length - 1")
                .vertical-line

  //- Add Note Dialog
  add-log-note-dialog(v-if="showAddNoteDialog" :open.sync="showAddNoteDialog" :log="selectedLog" @saved="onNoteSaved")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddLogNoteDialog from './AddLogNoteDialog.vue'

export default {
  props: {
    open: { type: Boolean, default: false },
    invoicePid: { type: String, default: null }
  },
  components: { AddLogNoteDialog },
  data: () => ({
    isLoading: false,
    logs: [],
    showAddNoteDialog: false,
    selectedLog: null
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      displaySize: 'ui/getDisplaySize'
    }),
    showDrawer: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    drawerHeight () {
      switch (this.displaySize) {
        case 'xx-small':
          return '149vh'
        case 'x-small':
          return '133vh'
        case 'small':
          return '125vh'
        case 'medium':
          return '111vh'
        case 'large':
          return '100vh'
        case 'x-large':
          return '91vh'
        default:
          return '100vh'
      }
    }
  },
  watch: {
    open: {
      immediate: true,
      handler (val) {
        if (val && this.invoicePid) {
          this.fetchLogs()
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchLogs () {
      this.isLoading = true
      try {
        const params = {
          company: this.company.id,
          app_model: 'form.lead',
          object_id: this.invoicePid,
          page_size: -1
        }
        const resp = await this.$axios.get('/actions', { params })
        this.logs = resp.data.results || []
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    getUserName (item) {
      if (item && item.user) {
        if (item.user.first_name || item.user.last_name) {
          return `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim()
        }
        if (item.user.name) return item.user.name
      }
      return 'System'
    },
    getNoteUserName (noteBy) {
      if (noteBy) {
        if (noteBy.first_name || noteBy.last_name) {
          return `${noteBy.first_name || ''} ${noteBy.last_name || ''}`.trim()
        }
        if (noteBy.name) return noteBy.name
      }
      return 'System'
    },
    getIcon (tag) {
      switch (tag) {
        case 'add_transaction':
          return '$yzi-credit-card-01'
        case 'edit_transaction':
        case 'edit_tax':
          return '$yzi-edit-01'
        case 'delete_transaction':
        case 'delete_tax':
        case 'delete_adjustment':
          return 'mdi-close'
        case 'add_tax':
        case 'add_adjustment':
          return 'mdi-percent'
        case 'complete_consultation':
          return '$yzi-user-check-01'
        case 'complete_visit':
          return '$yzi-receipt-check'
        case 'add_treatment':
          return '$yzi-medical-cross'
        case 'delete_treatment':
          return 'mdi-close'
        case 'edit_treatment':
          return '$yzi-edit-01'
        default:
          return 'mdi-receipt'
      }
    },
    onAddNote (log) {
      this.selectedLog = log
      this.showAddNoteDialog = true
    },
    onNoteSaved () {
      this.fetchLogs()
    },
    onEditNote (log) {
      this.selectedLog = { ...log, isEdit: true }
      this.showAddNoteDialog = true
    },
    async onDeleteNote (log) {
      try {
        await this.$axios.patch(`/actions/${log.id}`, { note: null })
        this.showMessage(['success', 'Note deleted successfully'])
        this.fetchLogs()
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    closeDrawer () {
      this.showDrawer = false
    }
  }
}
</script>

<style lang="scss" scoped>
.activity-log-drawer {
  box-shadow: -11px 0px 44.3px 0px rgba(0, 0, 0, 0.25) !important;
}

.v-navigation-drawer--is-mobile:not(.v-navigation-drawer--close),
.v-navigation-drawer--temporary:not(.v-navigation-drawer--close) {
  box-shadow: -11px 0px 44.3px 0px rgba(0, 0, 0, 0.25) !important;
}

.log-icon {
  width: 24px;
  height: 24px;
  min-width: 24px;
  background-color: var(--v-neutral300);
}

.log-entry-card {
  border-color: var(--v-neutral300) !important;
}

.connector-line {
  width: 22px;
  height: 12px;
  position: relative;
  margin-left: 11px;

  .vertical-line {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: var(--v-neutral400);
    transform: translateX(-50%);
  }
}

.min-width-0 {
  min-width: 0;
}

.log-activity {
  word-break: break-word;
  line-height: 20px;
}

</style>
